import loadable from '@loadable/component';
import type {ComponentType} from 'react';

const Login = loadable(() => import('common/auth/Login'));
const Reports = loadable(() => import('pages/reports/Reports'));
const Blackouts = loadable(() => import('pages/blackouts/Blackouts'));
const Changelog = loadable(() => import('pages/changelog/Changelog'));
const Dashboard = loadable(() => import('pages/dashboard/Dashboard'));
const Inventory = loadable(() => import('pages/inventory/Inventory'));
const Accounting = loadable(() => import('pages/accounting/Accounting'));
const TransientRates = loadable(
    () => import('pages/rates/transient/TransientRates')
);
const EmployeeParking = loadable(
    () => import('pages/employee-parking/EmployeeParking')
);
const Airport = loadable(() => import('pages/rates/airport/Airport'));
const Monthly = loadable(() => import('pages/rates/monthly/Monthly'));
const Reservations = loadable(
    () => import('pages/reservations/all-reservations/Reservations')
);
const EventManagement = loadable(
    () => import('pages/event-management/EventManagement')
);

const EmailRecipients = loadable(
    () => import('pages/facilities/email-recipients/EmailRecipients')
);
const LocationInfo = loadable(
    () => import('pages/facilities/location-info/LocationInfo')
);
const DriverFeedback = loadable(
    () => import('pages/facilities/driver-feedback/DriverFeedback')
);
const TodaysReservations = loadable(
    () => import('pages/reservations/todays-reservations/TodaysReservations')
);
const EventDayOverview = loadable(
    () => import('pages/event-management/event-day-overview/EventDayOverview')
);
const ComparativeSnapshot = loadable(
    () => import('pages/rates/comparative-snapshot/ComparativeSnapshot')
);
const CompPasses = loadable(() => import('pages/comp-pass/CompPasses'));

export interface FacilitiesRoutes {
    facility: string;
    locationInfo: string;
    emailRecipients: string;
}

export interface RatesRoutes {
    rates: string;
    airport: string;
    monthly: string;
    standardRates: string;
    spotheroIQ: string;
    flexRates: string;
    transient: string;
    comparativeSnapshot: string;
}

export interface AppRoutes {
    login: string;
    reports: string;
    blackouts: string;
    dashboard: string;
    changelog: string;
    accounting: string;
    reservations: string;
    todaysReservations: string;
    compPasses: string;
    facilities: FacilitiesRoutes;
    driverFeedback: string;
    starRatingsAndReviews: string;
    inventory: string;
    eventManagement: string;
    eventManagementDayOverview: string;
    rates: RatesRoutes;
    employeeParking: string;
}

/**
 * Application route path constants.
 *
 * @memberof module:constants
 */
export const ROUTES: AppRoutes = {
    login: '/login',
    reports: '/refunds',
    blackouts: '/blackouts',
    dashboard: '/dashboard',
    changelog: '/changelog',
    accounting: '/accounting',
    reservations: '/reservations',
    todaysReservations: '/facilities/:facilityId/todays-reservations',
    compPasses: '/facilities/:facilityId/comp-passes',
    facilities: {
        facility: '/facilities/:facilityId?',
        locationInfo: '/facilities/:facilityId/location-info',
        emailRecipients: '/facilities/:facilityId/email-recipients',
    },
    driverFeedback: '/facilities/:facilityId/driver-feedback/:tabId',
    starRatingsAndReviews:
        '/facilities/:facilityId/driver-feedback/star-ratings-and-review-tags',
    inventory: '/facilities/:facilityId/inventory',
    eventManagement: '/event-management/:tabId?',
    eventManagementDayOverview: `/event-management/details/:date/:tabId?`,
    rates: {
        rates: '/facilities/:facilityId/rates',
        airport: '/facilities/:facilityId/rates/airport/:tabId?',
        monthly: '/facilities/:facilityId/rates/monthly',
        standardRates: '/facilities/:facilityId/rates/standard',
        spotheroIQ: '/facilities/:facilityId/rates/spothero-iq',
        flexRates: '/facilities/:facilityId/rates/flex-rates',
        transient: '/facilities/:facilityId/rates/transient-rates',
        comparativeSnapshot: '/facilities/:facilityId/comparative-snapshot',
    },
    employeeParking: '/employee-parking',
};

export interface AppRedirects {
    root: string;
    catchAll: string;
    reports: string;
    spotheroIQ: string;
    rates: string;
    microclimate: string;
    additionalAdmins: string;
    eventManagement: string;
    eventManagementDayOverview: string;
}

/**
 * Legacy paths that redirect to active routes.
 *
 * @memberof module:constants
 */
export const REDIRECTS: AppRedirects = {
    root: '/',
    catchAll: '*',
    reports: '/reports',
    spotheroIQ: '/spothero-iq',
    rates: '/rates',
    microclimate: '/facilities/:facilityId/microclimate',
    additionalAdmins: '/facilities/:facilityId/additional-admins',
    eventManagement: '/facilities/:facilityId/event-management/:tabId?',
    eventManagementDayOverview:
        '/facilities/:facilityId/event-management/details/:date/:tabId?',
};

export interface RouteMatch {
    params?: Record<string, string | undefined>;
}

/**
 * A single route entry in the application route configuration.
 * The order of entries in the array affects route match precedence.
 *
 * @memberof module:constants
 */
export interface RouteConfigEntry {
    component?: ComponentType;
    name?: string;
    path?: string | string[];
    exact?: boolean;
    protected?: boolean;
    trackingDisabled?: boolean;
    isMultiFacility?: (match: RouteMatch) => boolean;
    redirect?: string;
}

export const ROUTE_CONFIG: RouteConfigEntry[] = [
    {component: Login, path: ROUTES.login, exact: true, protected: false},
    {
        path: [
            REDIRECTS.spotheroIQ,
            `${REDIRECTS.spotheroIQ}/:tabId`,
            `${ROUTES.rates.spotheroIQ}/:tabId`,
            REDIRECTS.rates,
            ROUTES.rates.flexRates,
            ROUTES.rates.spotheroIQ,
            ROUTES.rates.standardRates,
        ],
        redirect: ROUTES.rates.transient,
    },
    {
        path: REDIRECTS.eventManagementDayOverview,
        redirect: ROUTES.eventManagementDayOverview,
    },
    {
        path: REDIRECTS.eventManagement,
        redirect: ROUTES.eventManagement,
    },
    {
        path: REDIRECTS.additionalAdmins,
        redirect: ROUTES.facilities.emailRecipients,
    },
    {
        component: Reports,
        name: 'Reports',
        path: ROUTES.reports,
        exact: true,
        protected: true,
        isMultiFacility: () => true,
    },
    {
        component: Blackouts,
        name: 'Blackouts',
        path: ROUTES.blackouts,
        exact: true,
        protected: true,
        isMultiFacility: () => true,
    },
    {
        component: Dashboard,
        name: 'Dashboard',
        path: ROUTES.dashboard,
        exact: true,
        protected: true,
        isMultiFacility: () => true,
    },
    {
        component: Changelog,
        name: 'Changelog',
        path: ROUTES.changelog,
        exact: true,
        protected: true,
        isMultiFacility: () => true,
    },
    {
        component: Accounting,
        name: 'Accounting',
        path: ROUTES.accounting,
        exact: true,
        protected: true,
        isMultiFacility: () => true,
    },
    {
        component: Reservations,
        name: 'Reservations',
        path: ROUTES.reservations,
        exact: true,
        protected: true,
        isMultiFacility: () => true,
    },
    {
        component: CompPasses,
        name: 'Comp Passes',
        path: ROUTES.compPasses,
        exact: true,
        protected: true,
    },
    {
        component: Inventory,
        name: 'Inventory',
        path: ROUTES.inventory,
        exact: true,
        protected: true,
    },
    {
        component: EventDayOverview,
        name: 'Event Day Overview',
        path: ROUTES.eventManagementDayOverview,
        exact: true,
        protected: true,
        isMultiFacility: () => true,
    },
    {
        component: EventManagement,
        name: 'Event Management',
        path: ROUTES.eventManagement,
        exact: true,
        protected: true,
        isMultiFacility: (match) => {
            // Only specific tabs within this route support multi-facility selection
            const multiFacilityTabs = ['past-events', 'calendar'];

            return multiFacilityTabs.includes(match?.params?.tabId ?? '');
        },
    },
    {
        component: Airport,
        name: 'Airport',
        path: ROUTES.rates.airport,
        exact: true,
        protected: true,
    },
    {
        component: Monthly,
        name: 'Monthly',
        path: ROUTES.rates.monthly,
        exact: true,
        protected: true,
    },
    {
        component: TransientRates,
        name: 'Transient Rates',
        path: ROUTES.rates.transient,
        protected: true,
        trackingDisabled: true,
    },
    {
        component: EmployeeParking,
        name: 'Employee Parking',
        path: ROUTES.employeeParking,
        protected: true,
        exact: true,
        isMultiFacility: () => true,
    },
    {
        path: [
            '/facilities/:facilityId/driver-feedback/driver-feedback', // bad alias
            '/facilities/:facilityId/driver-feedback/review-tags', // old tab
        ],
        redirect: ROUTES.starRatingsAndReviews,
    },
    {
        component: DriverFeedback,
        name: 'Driver Feedback',
        path: ROUTES.driverFeedback,
        exact: true,
        protected: true,
    },
    {
        path: ['/facilities/:facilityId/driver-feedback'],
        redirect: ROUTES.starRatingsAndReviews,
    },
    {
        component: TodaysReservations,
        name: 'Todays Reservations',
        path: ROUTES.todaysReservations,
        exact: true,
        protected: true,
    },
    {
        path: REDIRECTS.microclimate,
        redirect: ROUTES.facilities.locationInfo,
    },
    {
        component: ComparativeSnapshot,
        name: 'Comparative Snapshot',
        path: ROUTES.rates.comparativeSnapshot,
        exact: true,
        protected: true,
    },
    {
        component: LocationInfo,
        name: 'Facility Location Info',
        path: ROUTES.facilities.locationInfo,
        exact: true,
        protected: true,
    },
    {
        component: EmailRecipients,
        name: 'Facility Email Recipients',
        path: ROUTES.facilities.emailRecipients,
        exact: true,
        protected: true,
    },
    // setup redirects
    {
        path: ROUTES.facilities.facility,
        redirect: ROUTES.facilities.locationInfo,
    },
    {
        path: REDIRECTS.reports,
        redirect: ROUTES.reports,
    },
    {
        path: REDIRECTS.root,
        redirect: ROUTES.dashboard,
    },
    {
        path: REDIRECTS.catchAll,
        redirect: ROUTES.dashboard,
    },
];
